// Gates for not-yet-GA modules that are also licensed separately: a surface
// shows only when the deployment is licensed for the module (GET
// /auth/licensed-products) AND its PostHog rollout flag is on. One name
// serves as the license module, the PostHog flag and (as FF_*) the airgapped
// fallback, as with edge-connectors. The flag legs live in
// src/config/posthog; this module adds the license leg, so posthog stays
// free of the API client.
//
// Sidebar items and role-gated routes use the same pair declaratively:
// `_product: "<name>"` plus `_enabled: is<Name>Enabled` (TheSidebar,
// TheContent).

import {
  LLM_SCREENING_FLAG,
  OBSERVATION_PROGRAM_FLAG,
  SMART_CR_FLAG,
  useLlmScreeningFlag,
  useObservationProgramFlag,
  useSmartCrFlag,
} from "src/config/posthog";
import { useGetLicensedProduct } from "src/helpers/rbac-rules";

export interface RolloutGate {
  enabled: boolean;
  /** Both the license query and PostHog have answered. Until then
   * `enabled` reads false (fail-closed), so anything that ACTS on false (a
   * redirect, a not-enabled notice) must wait for `loaded`. */
  loaded: boolean;
}

/** Combine a rollout flag with the license module of the same name. A failed
 * license query reads as unlicensed (fail-closed) and counts as answered. */
export const useLicensedRolloutGate = (
  product: string,
  flag: RolloutGate,
): RolloutGate => {
  const {
    data: licensedProducts,
    isSuccess,
    isError,
  } = useGetLicensedProduct();
  const licensed =
    isSuccess &&
    Array.isArray(licensedProducts) &&
    licensedProducts.includes(product);
  return {
    enabled: flag.enabled && licensed,
    loaded: flag.loaded && (isSuccess || isError),
  };
};

// ── LLM screening ─────────────────────────────────────────────────────────

/** LLM screening is available: the cap-llm-screening license module AND the
 * cap-llm-screening rollout flag (FF_LLM_SCREENING without PostHog). */
export const useLlmScreeningGate = (): RolloutGate =>
  useLicensedRolloutGate(LLM_SCREENING_FLAG, useLlmScreeningFlag());

/** Boolean form for render-only gating, where a late false→true flip is
 * harmless (the UI simply appears once both answers arrive). Anything that
 * navigates or persists on the answer must use useLlmScreeningGate and
 * wait for `loaded`. */
export const useLlmScreeningEnabled = (): boolean =>
  useLlmScreeningGate().enabled;

// ── Smart CR ──────────────────────────────────────────────────────────────

/** Smart CR is available: the smart-cr license module AND the smart-cr
 * rollout flag (FF_SMART_CR without PostHog). */
export const useSmartCrGate = (): RolloutGate =>
  useLicensedRolloutGate(SMART_CR_FLAG, useSmartCrFlag());

/** Boolean form for render-only gating (an entry point that simply appears
 * once both answers arrive). */
export const useSmartCrEnabled = (): boolean => useSmartCrGate().enabled;

// ── Observation Program ───────────────────────────────────────────────────

/** The observation interview is available: the observation-program license
 * module AND the observation-program rollout flag (FF_OBSERVATION_PROGRAM
 * without PostHog). The rest of the Observation Program is not gated. */
export const useObservationInterviewGate = (): RolloutGate =>
  useLicensedRolloutGate(OBSERVATION_PROGRAM_FLAG, useObservationProgramFlag());
